import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../services/supabase';
import { toast } from 'react-toastify';
import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { generateInvoicePDF, generateInvoiceNumber } from '../utils/invoiceUtils';
import { compressBase64 } from '../utils/compressionUtils';

const SALES_EMAIL = 'sales@acamachine.com';

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [packageSearchQuery, setPackageSearchQuery] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // New filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Date filter options
  const dateFilterOptions = [
    { value: 'all', label: 'All Time' },
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 3 Months' },
    { value: 'thisMonth', label: 'This Month' },
  ];

  // Status filter options
  const statusFilterOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'paid', label: 'Paid' },
    { value: 'unpaid', label: 'Unpaid' },
    { value: 'overdue', label: 'Overdue' },
  ];
  const [referenceNumber, setReferenceNumber] = useState('');
    const [paymentType, setPaymentType] = useState('Check');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editReferenceNumber, setEditReferenceNumber] = useState('');
    const [editPaymentType, setEditPaymentType] = useState('Check');
    const [editNotes, setEditNotes] = useState('');
    const [editStatus, setEditStatus] = useState('unpaid');
const [editPaymentDate, setEditPaymentDate] = useState(new Date().toISOString().split('T')[0]);
const [isPdfLoading, setIsPdfLoading] = useState(false);
const [pdfLoadError, setPdfLoadError] = useState(false);
const [currentPdfPath, setCurrentPdfPath] = useState(null);
const [signedUrl, setSignedUrl] = useState(null);
const [expandedRows, setExpandedRows] = useState({});
const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
const [emailRecipient, setEmailRecipient] = useState('');
const [selectedInvoiceForEmail, setSelectedInvoiceForEmail] = useState(null);
const [isEmailLoading, setIsEmailLoading] = useState(false); // Loading state for email sending
const [emailSuccess, setEmailSuccess] = useState(false); // Success state for email sending
const [emailRecipients, setEmailRecipients] = useState([]); // Array of email addresses
const [currentEmailInput, setCurrentEmailInput] = useState(''); // Current email being typed
const [emailLogs, setEmailLogs] = useState([]); // Store email logs for all invoices
const [isEmailHistoryModalOpen, setIsEmailHistoryModalOpen] = useState(false); // Control email history modal
const [selectedEmailHistory, setSelectedEmailHistory] = useState([]); // Store email logs for the selected invoice

// Batch invoice creation state
const [isBatchMode, setIsBatchMode] = useState(false);
const [batchProgress, setBatchProgress] = useState(0);
const [batchTotal, setBatchTotal] = useState(0);
const [batchResults, setBatchResults] = useState([]); // { package_number, invoice_number, success, error }
const [isBatchComplete, setIsBatchComplete] = useState(false);
const [batchWasCancelled, setBatchWasCancelled] = useState(false);
const batchCancelledRef = React.useRef(false);

// Bulk email state
const [isBulkEmailModalOpen, setIsBulkEmailModalOpen] = useState(false);
const [isBulkEmailMode, setIsBulkEmailMode] = useState(false);
const [bulkEmailProgress, setBulkEmailProgress] = useState(0);
const [bulkEmailTotal, setBulkEmailTotal] = useState(0);
const [bulkEmailResults, setBulkEmailResults] = useState([]); // { invoice_number, customer, recipients, success, error }
const [isBulkEmailComplete, setIsBulkEmailComplete] = useState(false);
const [bulkEmailWasCancelled, setBulkEmailWasCancelled] = useState(false);
const bulkEmailCancelledRef = React.useRef(false);

// Bulk payment state
const [isBulkPaymentModalOpen, setIsBulkPaymentModalOpen] = useState(false);
const [bulkPaymentSelectedInvoices, setBulkPaymentSelectedInvoices] = useState([]);
const [bulkPaymentCustomerFilter, setBulkPaymentCustomerFilter] = useState('all');
const [bulkPaymentSearch, setBulkPaymentSearch] = useState('');
const [bulkPaymentType, setBulkPaymentType] = useState('Check');
const [bulkPaymentReference, setBulkPaymentReference] = useState('');
const [bulkPaymentDate, setBulkPaymentDate] = useState(new Date().toISOString().split('T')[0]);
const [isBulkPaymentProcessing, setIsBulkPaymentProcessing] = useState(false);

// Add these state variables after the existing useState declarations (around line 30)
const [isExportModalOpen, setIsExportModalOpen] = useState(false);
const [selectedCustomerForExport, setSelectedCustomerForExport] = useState('all');
const [fileType, setFileType] = useState('CSV');
const [isGeneratingExport, setIsGeneratingExport] = useState(false);

// Add these functions after the existing handler functions (around line 800)

// Get unique customers for dropdown
const getUniqueCustomers = () => {
  const uniqueCustomers = invoices.reduce((acc, invoice) => {
    // Check if customer_id exists and is not null/undefined
    if (invoice.customer_id && invoice.customer_nickname && 
        !acc.find(c => c.customer_id === invoice.customer_id)) {
      acc.push({
        customer_id: invoice.customer_id,
        customer_nickname: invoice.customer_nickname || `Customer ${invoice.customer_id}`
      });
    }
    return acc;
  }, []);
  
  console.log('Unique customers found:', uniqueCustomers);
  console.log('Sample invoices for debugging:', invoices.slice(0, 2).map(inv => ({
    customer_id: inv.customer_id,
    customer_nickname: inv.customer_nickname,
    invoice_number: inv.invoice_number
  })));
  
  return uniqueCustomers.sort((a, b) => a.customer_nickname.localeCompare(b.customer_nickname));
};
// Open export modal
const openExportModal = () => {
  setIsExportModalOpen(true);
  setSelectedCustomerForExport('all');
  setFileType('CSV');
};

// Close export modal
const closeExportModal = () => {
  setIsExportModalOpen(false);
  setSelectedCustomerForExport('all');
  setFileType('CSV');
  setIsGeneratingExport(false);
};

// Generate and download CSV
const handleGenerateExport = async () => {
  setIsGeneratingExport(true);
  
  try {
    // Filter invoices by selected customer
    
    // NEW CODE - REPLACE WITH THIS:
let exportInvoices = invoices;
if (selectedCustomerForExport !== 'all') {
  // Handle both string and number customer IDs
  const selectedCustomerId = selectedCustomerForExport;
  exportInvoices = invoices.filter(inv => {
    // Compare both as strings and numbers to handle different data types
    return inv.customer_id == selectedCustomerId || 
           String(inv.customer_id) === String(selectedCustomerId);
  });
  
  console.log('Selected customer ID:', selectedCustomerId, 'Type:', typeof selectedCustomerId);
  console.log('All invoice customer IDs:', invoices.map(inv => ({ id: inv.customer_id, type: typeof inv.customer_id, nickname: inv.customer_nickname })));
  console.log('Filtered invoices:', exportInvoices.length);
  console.log('Filtered invoice numbers:', exportInvoices.map(inv => inv.invoice_number));
}
    
    if (exportInvoices.length === 0) {
      toast.error('No invoices found for the selected criteria.');
      setIsGeneratingExport(false);
      return;
    }
    
    // Generate CSV content
    const csvHeaders = [
      'Invoice Number',
      'Customer',
      'Package Number',
      'PO Number',
      'Total Quantity',
      'Total Amount',
      'Status',
      'Created Date',
      'Due Date',
      'Payment Date',
      'Payment Type',
      'Reference Number',
      'Notes'
    ];
    
    const csvRows = exportInvoices.map(invoice => [
      invoice.invoice_number,
      invoice.customer_nickname,
      invoice.package_number,
      invoice.po_number || '',
      invoice.total_quantity,
      invoice.total_amount.toFixed(2),
      invoice.status,
      new Date(invoice.created_at).toLocaleDateString(),
      new Date(invoice.net_due_date).toLocaleDateString(),
      invoice.payment_date ? new Date(invoice.payment_date).toLocaleDateString() : '',
      invoice.payment_type || '',
      invoice.reference_number || '',
      invoice.notes || ''
    ]);
    
    // Convert to CSV string
    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => 
        row.map(field => 
          // Escape commas and quotes in fields
          typeof field === 'string' && (field.includes(',') || field.includes('"')) 
            ? `"${field.replace(/"/g, '""')}"` 
            : field
        ).join(',')
      )
    ].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Generate filename
    const customerName = selectedCustomerForExport === 'all' 
      ? 'All_Customers' 
      : invoices.find(inv => inv.customer_id === selectedCustomerForExport)?.customer_nickname || 'Customer';
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `Invoices_${customerName}_${timestamp}.csv`);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`CSV exported successfully! ${exportInvoices.length} invoices included.`);
    closeExportModal();
    
  } catch (error) {
    console.error('Error generating export:', error);
    toast.error(`Failed to generate export: ${error.message}`);
  } finally {
    setIsGeneratingExport(false);
  }
};


  document.title = 'Invoices';

  useEffect(() => {
    const fetchData = async () => {
        setLoading(true);
        try {
          // Fetch all invoices
          const { data: invoicesData, error: invoicesError } = await supabase
            .from('invoices')
            .select(`
              invoice_number, package_number, customer_id, total_quantity, total_amount, 
              status, pdf_path, reference_number, payment_date, created_at, net_due_date, 
              payment_type, notes,
              customers!customer_id(nickname, billing_contact_email, billing_contact_emails, shipping_contact_email)
            `)
            .order('created_at', { ascending: false });
          if (invoicesError) throw new Error(`Invoices fetch error: ${invoicesError.message}`);

          // Fetch all deliveries
          const { data: deliveriesData, error: deliveriesError } = await supabase
            .from('deliveries')
            .select(`
              package_number, quantity, invoice_number, po_number, po_line_number, part_number,
              job_id, kanban_job_id, ship_date, tracking_number, ship_by,
              jobs!left(unit_price, customer_id, customers!left(nickname, billing_contact_email, billing_contact_emails, shipping_contact_email)),
kanban_jobs!left(unit_price, description, customers!left(id, nickname, billing_contact_email, billing_contact_emails, shipping_contact_email)),
              parts!part_number(description)
            `);
          if (deliveriesError) throw new Error(`Deliveries fetch error: ${deliveriesError.message}`);
      
          // Fetch email logs
          const { data: emailLogsData, error: emailLogsError } = await supabase
            .from('emails')
            .select('id, invoice_number, recipients, sent_at, status, error_message')
            .order('sent_at', { ascending: false });
          if (emailLogsError) throw new Error(`Email logs fetch error: ${emailLogsError.message}`);
          setEmailLogs(emailLogsData || []);
      
          // Format invoices and attach corresponding deliveries using package_number
          const formattedInvoices = invoicesData.map(invoice => {
            // Find deliveries associated with this invoice using package_number
            const associatedDeliveries = deliveriesData
              .filter(delivery => delivery.package_number === invoice.package_number)
              .map(delivery => ({
                ...delivery,
                description: delivery.parts?.description || delivery.kanban_jobs?.description || 'N/A',
                jobs: delivery.jobs || {},
                kanban_jobs: delivery.kanban_jobs || {},
              }));
      
            // Find email logs for this invoice
            const invoiceEmailLogs = emailLogsData.filter(log => log.invoice_number === invoice.invoice_number);
      
            return {
              ...invoice,
              customer_nickname: invoice.customers?.nickname || '-',
              billing_contact_email: invoice.customers?.billing_contact_email || '',
              billing_contact_emails: invoice.customers?.billing_contact_emails || [],
              shipping_contact_email: invoice.customers?.shipping_contact_email || '',
              po_number: associatedDeliveries[0]?.po_number || 'N/A',
              deliveries: associatedDeliveries || [], // Ensure deliveries is always an array
              email_logs: invoiceEmailLogs, // Attach email logs to the invoice
            };
          });
          setInvoices(formattedInvoices);
          setFilteredInvoices(formattedInvoices);
      
          // Group deliveries by package_number and filter out invoiced packages for the create invoice modal
          const groupedPackages = deliveriesData.reduce((acc, delivery) => {
            const packageNumber = delivery.package_number;
            if (!acc[packageNumber]) {
              acc[packageNumber] = {
                package_number: packageNumber,
                po_number: delivery.po_number,
                deliveries: [],
                total_quantity: 0,
                total_amount: 0,
                customer: null,
                customer_nickname: '',
                has_invoice: false,
              };
            }
            if (delivery.jobs) {
              delivery.jobs.customers = delivery.jobs.customers || {};
            } else if (delivery.kanban_jobs) {
              delivery.kanban_jobs.customers = delivery.kanban_jobs.customers || {};
            }
      
            delivery.description = delivery.parts?.description || delivery.kanban_jobs?.description || 'N/A';
            acc[packageNumber].deliveries.push(delivery);
            acc[packageNumber].total_quantity += delivery.quantity;
      
            const unitPrice = delivery.jobs?.unit_price || delivery.kanban_jobs?.unit_price || 0;
            acc[packageNumber].total_amount += delivery.quantity * unitPrice;
      
            if (!acc[packageNumber].customer) {
              acc[packageNumber].customer = delivery.jobs?.customers || delivery.kanban_jobs?.customers || {};
              acc[packageNumber].customer_nickname = acc[packageNumber].customer.nickname || '-';
            }
      
            if (delivery.invoice_number) {
              acc[packageNumber].has_invoice = true;
            }
      
            return acc;
          }, {});
      
          const packageArray = Object.values(groupedPackages).filter(pkg => !pkg.has_invoice);
          setPackages(packageArray);
          setFilteredPackages(packageArray);
        } catch (err) {
          console.error('Error fetching data:', err.message);
          toast.error(`Failed to fetch data: ${err.message}`);
          setInvoices([]);
          setPackages([]);
          setFilteredInvoices([]);
          setFilteredPackages([]);
          setEmailLogs([]);
        } finally {
          setLoading(false);
        }
      };
  
    fetchData();
  }, []);


  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all invoices
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('invoices')
        .select(`
          invoice_number, package_number, customer_id, total_quantity, total_amount,
          status, pdf_path, reference_number, payment_date, created_at, net_due_date,
          payment_type, notes,
          customers!customer_id(nickname, billing_contact_email, billing_contact_emails, shipping_contact_email)
        `)
        .order('created_at', { ascending: false });
      if (invoicesError) throw new Error(`Invoices fetch error: ${invoicesError.message}`);

      // Fetch all deliveries
      const { data: deliveriesData, error: deliveriesError } = await supabase
        .from('deliveries')
        .select(`
          package_number, quantity, invoice_number, po_number, po_line_number, part_number,
          job_id, kanban_job_id, ship_date, tracking_number, ship_by,
          jobs!left(unit_price, customer_id, customers!left(nickname, billing_contact_email, billing_contact_emails, shipping_contact_email)),
kanban_jobs!left(unit_price, description, customers!left(id, nickname, billing_contact_email, billing_contact_emails, shipping_contact_email)),
          parts!part_number(description)
        `);
      if (deliveriesError) throw new Error(`Deliveries fetch error: ${deliveriesError.message}`);
  
      // Fetch email logs
      const { data: emailLogsData, error: emailLogsError } = await supabase
        .from('emails')
        .select('id, invoice_number, recipients, sent_at, status, error_message')
        .order('sent_at', { ascending: false });
      if (emailLogsError) throw new Error(`Email logs fetch error: ${emailLogsError.message}`);
      setEmailLogs(emailLogsData || []);
  
      // Format invoices and attach corresponding deliveries using package_number
      const formattedInvoices = invoicesData.map(invoice => {
        // Find deliveries associated with this invoice using package_number
        const associatedDeliveries = deliveriesData
          .filter(delivery => delivery.package_number === invoice.package_number)
          .map(delivery => ({
            ...delivery,
            description: delivery.parts?.description || delivery.kanban_jobs?.description || 'N/A',
            jobs: delivery.jobs || {},
            kanban_jobs: delivery.kanban_jobs || {},
          }));
  
        // Find email logs for this invoice
        const invoiceEmailLogs = emailLogsData.filter(log => log.invoice_number === invoice.invoice_number);
  
        return {
          ...invoice,
          customer_nickname: invoice.customers?.nickname || '-',
          billing_contact_email: invoice.customers?.billing_contact_email || '',
          billing_contact_emails: invoice.customers?.billing_contact_emails || [],
          shipping_contact_email: invoice.customers?.shipping_contact_email || '',
          po_number: associatedDeliveries[0]?.po_number || 'N/A',
          deliveries: associatedDeliveries || [],
          email_logs: invoiceEmailLogs,
        };
      });
      setInvoices(formattedInvoices);
      setFilteredInvoices(formattedInvoices);
  
      // Group deliveries by package_number and filter out invoiced packages for the create invoice modal
      const groupedPackages = deliveriesData.reduce((acc, delivery) => {
        const packageNumber = delivery.package_number;
        if (!acc[packageNumber]) {
          acc[packageNumber] = {
            package_number: packageNumber,
            po_number: delivery.po_number,
            deliveries: [],
            total_quantity: 0,
            total_amount: 0,
            customer: null,
            customer_nickname: '',
            has_invoice: false,
          };
        }
        if (delivery.jobs) {
          delivery.jobs.customers = delivery.jobs.customers || {};
        } else if (delivery.kanban_jobs) {
          delivery.kanban_jobs.customers = delivery.kanban_jobs.customers || {};
        }
  
        delivery.description = delivery.parts?.description || delivery.kanban_jobs?.description || 'N/A';
        acc[packageNumber].deliveries.push(delivery);
        acc[packageNumber].total_quantity += delivery.quantity;
  
        const unitPrice = delivery.jobs?.unit_price || delivery.kanban_jobs?.unit_price || 0;
        acc[packageNumber].total_amount += delivery.quantity * unitPrice;
  
        if (!acc[packageNumber].customer) {
          acc[packageNumber].customer = delivery.jobs?.customers || delivery.kanban_jobs?.customers || {};
          acc[packageNumber].customer_nickname = acc[packageNumber].customer.nickname || '-';
        }
  
        if (delivery.invoice_number) {
          acc[packageNumber].has_invoice = true;
        }
  
        return acc;
      }, {});
  
      const packageArray = Object.values(groupedPackages).filter(pkg => !pkg.has_invoice);
      setPackages(packageArray);
      setFilteredPackages(packageArray);
    } catch (err) {
      console.error('Error fetching data:', err.message);
      toast.error(`Failed to fetch data: ${err.message}`);
      setInvoices([]);
      setPackages([]);
      setFilteredInvoices([]);
      setFilteredPackages([]);
      setEmailLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort invoices
  useEffect(() => {
    let filtered = [...invoices];

    // Apply date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      let startDate;

      if (dateFilter === 'thisMonth') {
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      } else {
        const days = parseInt(dateFilter);
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - days);
      }

      filtered = filtered.filter(inv => {
        const invoiceDate = new Date(inv.created_at);
        return invoiceDate >= startDate && invoiceDate <= today;
      });
    }

    // Apply customer filter
    if (filterCustomer) {
      filtered = filtered.filter(inv => String(inv.customer_id) === String(filterCustomer));
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'paid') {
        filtered = filtered.filter(inv => inv.status === 'paid');
      } else if (filterStatus === 'unpaid') {
        filtered = filtered.filter(inv => inv.status === 'unpaid');
      } else if (filterStatus === 'overdue') {
        const today = new Date();
        filtered = filtered.filter(inv => inv.status === 'unpaid' && new Date(inv.net_due_date) < today);
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      if (sortField === 'total_quantity' || sortField === 'total_amount') {
        aValue = aValue || 0;
        bValue = bValue || 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (sortField === 'created_at' || sortField === 'net_due_date') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });

    setFilteredInvoices(filtered);
  }, [filterStatus, dateFilter, filterCustomer, sortField, sortDirection, invoices]);

  // Get unique customers for filter dropdown
  const customers = useMemo(() => {
    const unique = invoices.reduce((acc, inv) => {
      if (inv.customer_id && inv.customer_nickname && !acc.find(c => c.id === inv.customer_id)) {
        acc.push({ id: inv.customer_id, nickname: inv.customer_nickname });
      }
      return acc;
    }, []);
    return unique.sort((a, b) => a.nickname.localeCompare(b.nickname));
  }, [invoices]);

  // Get unsent invoices (no email_logs or empty email_logs)
  const unsentInvoices = useMemo(() => {
    return invoices.filter(inv => {
      const hasBeenSent = inv.email_logs && inv.email_logs.length > 0 &&
        inv.email_logs.some(log => log.status === 'sent');
      // Check for emails in array first, then fall back to legacy field
      const hasRecipient = (inv.billing_contact_emails && inv.billing_contact_emails.length > 0) ||
        inv.billing_contact_email ||
        inv.shipping_contact_email;
      return !hasBeenSent && hasRecipient;
    });
  }, [invoices]);

  // Get unpaid invoices for bulk payment
  const unpaidInvoices = useMemo(() => {
    return invoices.filter(inv => inv.status === 'unpaid');
  }, [invoices]);

  // Filter unpaid invoices by customer and search for bulk payment modal
  const filteredUnpaidInvoices = useMemo(() => {
    let filtered = unpaidInvoices;
    if (bulkPaymentCustomerFilter !== 'all') {
      filtered = filtered.filter(inv => inv.customer_nickname === bulkPaymentCustomerFilter);
    }
    if (bulkPaymentSearch.trim()) {
      const search = bulkPaymentSearch.toLowerCase().trim();
      filtered = filtered.filter(inv => inv.invoice_number.toLowerCase().includes(search));
    }
    return filtered;
  }, [unpaidInvoices, bulkPaymentCustomerFilter, bulkPaymentSearch]);

  // Filter packages in the Create Invoice modal
  useEffect(() => {
    let filtered = [...packages];
    if (packageSearchQuery) {
      filtered = filtered.filter(pkg =>
        pkg.package_number.toLowerCase().includes(packageSearchQuery.toLowerCase())
      );
    }
    setFilteredPackages(filtered);
  }, [packageSearchQuery, packages]);

  // Calculate summary metrics
  const accountsReceivable = invoices
    .filter(inv => inv.status === 'unpaid')
    .reduce((sum, inv) => sum + inv.total_amount, 0);
  const cashReceived = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + inv.total_amount, 0);
  const totalInvoiced = invoices.reduce((sum, inv) => sum + inv.total_amount, 0);
  const overdueAmount = invoices
    .filter(inv => inv.status === 'unpaid' && new Date(inv.net_due_date) < new Date())
    .reduce((sum, inv) => sum + inv.total_amount, 0);

  


const handleCreateInvoice = async () => {
  if (!selectedPackage) {
    toast.error('Please select a package to invoice.');
    return;
  }

  try {
    // Validate customer exists
    const customerId = selectedPackage.deliveries[0]?.jobs?.customer_id || selectedPackage.deliveries[0]?.kanban_jobs?.customers?.id;

    if (!customerId) {
      toast.error('Could not find a customer for this package. The package data may be corrupt or incomplete.');
      console.error('Missing customerId for package:', selectedPackage.package_number, selectedPackage);
      return;
    }

    // Validate all deliveries in the package belong to the same customer
    const allSameCustomer = selectedPackage.deliveries.every(delivery => {
      const deliveryCustomerId = delivery.jobs?.customer_id || delivery.kanban_jobs?.customers?.id;
      return deliveryCustomerId === customerId;
    });

    if (!allSameCustomer) {
      toast.error('Package contains items from different customers. Cannot create a single invoice.');
      console.error('Mixed customers in package:', selectedPackage.package_number);
      return;
    }

    // Check for duplicate invoice (prevent double invoicing same package)
    const { data: existingInvoice, error: existingError } = await supabase
      .from('invoices')
      .select('invoice_number')
      .eq('package_number', selectedPackage.package_number)
      .maybeSingle();

    if (existingError) {
      console.error('Error checking for existing invoice:', existingError);
      toast.error('Failed to verify package invoice status. Please try again.');
      return;
    }

    if (existingInvoice) {
      toast.error(`Package ${selectedPackage.package_number} has already been invoiced as ${existingInvoice.invoice_number}`);
      // Refresh data to update the packages list
      fetchData();
      setIsCreateModalOpen(false);
      setSelectedPackage(null);
      return;
    }

    // Generate a unique invoice number
    const invoiceNumber = await generateInvoiceNumber();

    // Fetch complete customer data for PDF generation and net terms
    const { data: customerData, error: customerError } = await supabase
      .from('customers')
      .select('*')
      .eq('id', customerId) // <<< USE THE VALIDATED customerId
      .single();
    if (customerError) throw new Error(`Failed to fetch customer data: ${customerError.message}`);

    // Create the invoice record
    const createdAt = new Date();
    const netTerms = customerData.net_terms || 15;
    const netDueDate = new Date(createdAt);
    netDueDate.setDate(netDueDate.getDate() + netTerms);

    const { data: invoiceData, error: invoiceError } = await supabase
      .from('invoices')
      .insert({
        invoice_number: invoiceNumber,
        package_number: selectedPackage.package_number,
        customer_id: customerId, // <<< USE THE VALIDATED customerId
        total_quantity: selectedPackage.total_quantity,
        total_amount: selectedPackage.total_amount,
        status: 'unpaid',
        created_at: createdAt.toISOString(),
        net_due_date: netDueDate.toISOString(),
        notes: null,
      })
      .select()
      .single();
    if (invoiceError) throw new Error(`Failed to create invoice: ${invoiceError.message}`);

    // ... The rest of your function remains the same ...
    // Update all deliveries in the package with the invoice number
    const { error: updateError } = await supabase
      .from('deliveries')
      .update({ invoice_number: invoiceNumber })
      .eq('package_number', selectedPackage.package_number);
    if (updateError) throw new Error(`Failed to update deliveries: ${updateError.message}`);

    // Fetch part_number_master for each delivery
    const partNumbers = selectedPackage.deliveries.map(delivery => delivery.part_number);
    const { data: partsData, error: partsError } = await supabase
      .from('parts')
      .select('part_number, part_number_master')
      .in('part_number', partNumbers);
    if (partsError) throw new Error(`Failed to fetch parts data: ${partsError.message}`);

    // Create a mapping of part_number to part_number_master
    const partNumberMap = partsData.reduce((acc, part) => {
      acc[part.part_number] = part.part_number_master || part.part_number;
      return acc;
    }, {});

    // Prepare the package for PDF generation
    const invoicePackage = {
      package_number: selectedPackage.package_number,
      po_number: selectedPackage.po_number,
      deliveries: selectedPackage.deliveries.map(delivery => ({
        ...delivery,
        part_number: partNumberMap[delivery.part_number] || delivery.part_number,
      })),
      total_quantity: selectedPackage.total_quantity,
      total_amount: selectedPackage.total_amount,
      customer: customerData || {},
      net_terms: netTerms,
      notes: null,
    };

    // Generate and upload the PDF using the utility function
    const pdfBlob = await generateInvoicePDF(invoicePackage, invoiceNumber, createdAt, netDueDate);
    const pdfFilePath = `invoices/${invoiceNumber}.pdf`;
    const { error: uploadError } = await supabase.storage
      .from('invoices')
      .upload(pdfFilePath, pdfBlob, { contentType: 'application/pdf' });
    if (uploadError) throw new Error(`Failed to upload invoice PDF: ${uploadError.message}`);

    // Update the invoice with the PDF path
    const { error: updateInvoiceError } = await supabase
      .from('invoices')
      .update({ pdf_path: pdfFilePath })
      .eq('invoice_number', invoiceNumber);
    if (updateInvoiceError) throw new Error(`Failed to update invoice with PDF path: ${updateInvoiceError.message}`);
    
    // As recommended previously, you can remove the manual state updates below
    // and rely on the fetchData() call to refresh the UI.
    fetchData(); // Refetch data to ensure consistency
    toast.success(`Invoice ${invoiceNumber} created successfully!`);
    
    // You can close the modal after success
    setIsCreateModalOpen(false);
    setSelectedPackage(null);

  } catch (err) {
    console.error('Error creating invoice:', err);
    toast.error(`Failed to create invoice: ${err.message}`);
  }
};

// Batch create all uninvoiced packages
const handleCreateAllInvoices = async () => {
  if (filteredPackages.length === 0) {
    toast.error('No uninvoiced packages to process.');
    return;
  }

  // Enter batch mode
  batchCancelledRef.current = false;
  setBatchWasCancelled(false);
  setIsBatchMode(true);
  setIsBatchComplete(false);
  setBatchProgress(0);
  setBatchTotal(filteredPackages.length);
  setBatchResults([]);

  const results = [];

  // Get the starting invoice number
  let currentInvoiceNumber;
  try {
    const { data: existingInvoices, error } = await supabase
      .from('invoices')
      .select('invoice_number')
      .order('invoice_number', { ascending: false })
      .limit(1);
    if (error) throw error;

    let count = 5110;
    if (existingInvoices && existingInvoices.length > 0) {
      const match = existingInvoices[0].invoice_number.match(/INV-(\d+)/);
      if (match) {
        count = Math.max(parseInt(match[1], 10) + 1, 5110);
      }
    }
    currentInvoiceNumber = count;
  } catch (err) {
    toast.error('Failed to get starting invoice number');
    setIsBatchMode(false);
    return;
  }

  // Process each package sequentially
  for (let i = 0; i < filteredPackages.length; i++) {
    // Check if cancelled
    if (batchCancelledRef.current) {
      break;
    }

    const pkg = filteredPackages[i];
    setBatchProgress(i + 1);

    const invoiceNumber = `INV-${String(currentInvoiceNumber).padStart(4, '0')}`;

    try {
      // Validate customer exists
      const customerId = pkg.deliveries[0]?.jobs?.customer_id || pkg.deliveries[0]?.kanban_jobs?.customers?.id;
      if (!customerId) {
        throw new Error('No customer found');
      }

      // Validate all deliveries belong to same customer
      const allSameCustomer = pkg.deliveries.every(delivery => {
        const deliveryCustomerId = delivery.jobs?.customer_id || delivery.kanban_jobs?.customers?.id;
        return deliveryCustomerId === customerId;
      });
      if (!allSameCustomer) {
        throw new Error('Mixed customers in package');
      }

      // Check for duplicate invoice
      const { data: existingInvoice } = await supabase
        .from('invoices')
        .select('invoice_number')
        .eq('package_number', pkg.package_number)
        .maybeSingle();
      if (existingInvoice) {
        throw new Error(`Already invoiced as ${existingInvoice.invoice_number}`);
      }

      // Fetch customer data
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();
      if (customerError) throw new Error('Failed to fetch customer');

      // Create the invoice record
      const createdAt = new Date();
      const netTerms = customerData.net_terms || 15;
      const netDueDate = new Date(createdAt);
      netDueDate.setDate(netDueDate.getDate() + netTerms);

      const { error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          invoice_number: invoiceNumber,
          package_number: pkg.package_number,
          customer_id: customerId,
          total_quantity: pkg.total_quantity,
          total_amount: pkg.total_amount,
          status: 'unpaid',
          created_at: createdAt.toISOString(),
          net_due_date: netDueDate.toISOString(),
          notes: null,
        });
      if (invoiceError) throw new Error('Failed to create invoice');

      // Update deliveries with invoice number
      const { error: updateError } = await supabase
        .from('deliveries')
        .update({ invoice_number: invoiceNumber })
        .eq('package_number', pkg.package_number);
      if (updateError) throw new Error('Failed to update deliveries');

      // Fetch parts data for PDF
      const partNumbers = pkg.deliveries.map(d => d.part_number);
      const { data: partsData } = await supabase
        .from('parts')
        .select('part_number, part_number_master')
        .in('part_number', partNumbers);

      const partNumberMap = (partsData || []).reduce((acc, part) => {
        acc[part.part_number] = part.part_number_master || part.part_number;
        return acc;
      }, {});

      // Prepare package for PDF
      const invoicePackage = {
        package_number: pkg.package_number,
        po_number: pkg.po_number,
        deliveries: pkg.deliveries.map(d => ({
          ...d,
          part_number: partNumberMap[d.part_number] || d.part_number,
        })),
        total_quantity: pkg.total_quantity,
        total_amount: pkg.total_amount,
        customer: customerData,
        net_terms: netTerms,
        notes: null,
      };

      // Generate and upload PDF
      const pdfBlob = await generateInvoicePDF(invoicePackage, invoiceNumber, createdAt, netDueDate);
      const pdfFilePath = `invoices/${invoiceNumber}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from('invoices')
        .upload(pdfFilePath, pdfBlob, { contentType: 'application/pdf' });
      if (uploadError) throw new Error('Failed to upload PDF');

      // Update invoice with PDF path
      await supabase
        .from('invoices')
        .update({ pdf_path: pdfFilePath })
        .eq('invoice_number', invoiceNumber);

      results.push({
        package_number: pkg.package_number,
        invoice_number: invoiceNumber,
        customer: pkg.customer_nickname,
        success: true,
        error: null,
      });

      currentInvoiceNumber++;
    } catch (err) {
      results.push({
        package_number: pkg.package_number,
        invoice_number: invoiceNumber,
        customer: pkg.customer_nickname,
        success: false,
        error: err.message,
      });
      // Still increment invoice number to avoid gaps
      currentInvoiceNumber++;
    }

    setBatchResults([...results]);
  }

  setIsBatchComplete(true);

  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;
  const wasCancelled = batchCancelledRef.current;
  setBatchWasCancelled(wasCancelled);

  if (wasCancelled) {
    toast.info(`Stopped. Created ${successCount} invoices.`);
  } else if (failCount === 0) {
    toast.success(`Created ${successCount} invoices successfully!`);
  } else {
    toast.warn(`Created ${successCount} invoices. ${failCount} failed.`);
  }

  // Refresh data
  fetchData();
};

// Cancel batch processing
const handleCancelBatch = () => {
  batchCancelledRef.current = true;
};

// Reset batch mode when closing modal
const closeBatchMode = () => {
  batchCancelledRef.current = false;
  setBatchWasCancelled(false);
  setIsBatchMode(false);
  setIsBatchComplete(false);
  setBatchProgress(0);
  setBatchTotal(0);
  setBatchResults([]);
  setIsCreateModalOpen(false);
  setSelectedPackage(null);
};

// Bulk email functions
const openBulkEmailModal = () => {
  setIsBulkEmailModalOpen(true);
  setIsBulkEmailMode(false);
  setIsBulkEmailComplete(false);
  setBulkEmailProgress(0);
  setBulkEmailResults([]);
  setBulkEmailWasCancelled(false);
};

const closeBulkEmailModal = () => {
  bulkEmailCancelledRef.current = false;
  setBulkEmailWasCancelled(false);
  setIsBulkEmailMode(false);
  setIsBulkEmailComplete(false);
  setBulkEmailProgress(0);
  setBulkEmailTotal(0);
  setBulkEmailResults([]);
  setIsBulkEmailModalOpen(false);
};

const handleCancelBulkEmail = () => {
  bulkEmailCancelledRef.current = true;
};

const handleBulkSendEmails = async () => {
  if (unsentInvoices.length === 0) {
    toast.error('No unsent invoices to email.');
    return;
  }

  // Enter bulk email mode
  bulkEmailCancelledRef.current = false;
  setBulkEmailWasCancelled(false);
  setIsBulkEmailMode(true);
  setIsBulkEmailComplete(false);
  setBulkEmailProgress(0);
  setBulkEmailTotal(unsentInvoices.length);
  setBulkEmailResults([]);

  const results = [];

  for (let i = 0; i < unsentInvoices.length; i++) {
    // Check if cancelled
    if (bulkEmailCancelledRef.current) {
      break;
    }

    const invoice = unsentInvoices[i];
    setBulkEmailProgress(i + 1);

    // Get recipient emails (prefer billing_contact_emails array, fallback to legacy field, then shipping)
    // Always include sales@acamachine.com
    let recipientEmails = [SALES_EMAIL];
    if (invoice.billing_contact_emails && invoice.billing_contact_emails.length > 0) {
      recipientEmails = [...recipientEmails, ...invoice.billing_contact_emails];
    } else if (invoice.billing_contact_email) {
      recipientEmails = [...recipientEmails, invoice.billing_contact_email];
    } else if (invoice.shipping_contact_email) {
      recipientEmails = [...recipientEmails, invoice.shipping_contact_email];
    }

    // Only require at least one customer email besides sales
    if (recipientEmails.length === 1) {
      results.push({
        invoice_number: invoice.invoice_number,
        customer: invoice.customer_nickname,
        recipients: [],
        success: false,
        error: 'No email address',
      });
      setBulkEmailResults([...results]);
      continue;
    }

    try {
      // Download the PDF
      const { data: pdfData, error: pdfError } = await supabase.storage
        .from('invoices')
        .download(invoice.pdf_path);
      if (pdfError) throw new Error('Failed to download PDF');

      // Convert to base64
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read PDF'));
        reader.readAsDataURL(pdfData);
      });

      // Compress
      const compressedPdf = await compressBase64(pdfBase64, 8000);

      // Send email to all recipients
      const response = await fetch('https://eiojxadvwbyjehiowyhd.supabase.co/functions/v1/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceNumber: invoice.invoice_number,
          totalAmount: invoice.total_amount,
          dueDate: invoice.net_due_date,
          recipient: recipientEmails,
          pdfBase64: compressedPdf,
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send');
      }

      // Log successful send
      const { data: newEmailLog } = await supabase
        .from('emails')
        .insert({
          invoice_number: invoice.invoice_number,
          recipients: recipientEmails,
          status: 'sent',
          error_message: null,
        })
        .select()
        .single();

      // Update local state with new email log
      if (newEmailLog) {
        setInvoices(prev =>
          prev.map(inv =>
            inv.invoice_number === invoice.invoice_number
              ? { ...inv, email_logs: [newEmailLog, ...(inv.email_logs || [])] }
              : inv
          )
        );
      }

      results.push({
        invoice_number: invoice.invoice_number,
        customer: invoice.customer_nickname,
        recipients: recipientEmails,
        success: true,
        error: null,
      });
    } catch (err) {
      // Log failed send
      await supabase
        .from('emails')
        .insert({
          invoice_number: invoice.invoice_number,
          recipients: recipientEmails,
          status: 'failed',
          error_message: err.message,
        });

      results.push({
        invoice_number: invoice.invoice_number,
        customer: invoice.customer_nickname,
        recipients: recipientEmails,
        success: false,
        error: err.message,
      });
    }

    setBulkEmailResults([...results]);
  }

  setIsBulkEmailComplete(true);

  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;
  const wasCancelled = bulkEmailCancelledRef.current;
  setBulkEmailWasCancelled(wasCancelled);

  if (wasCancelled) {
    toast.info(`Stopped. Sent ${successCount} emails.`);
  } else if (failCount === 0) {
    toast.success(`Sent ${successCount} emails successfully!`);
  } else {
    toast.warn(`Sent ${successCount} emails. ${failCount} failed.`);
  }

  // Refresh data
  fetchData();
};

const handleRecordPayment = async () => {
    if (!selectedInvoice || !referenceNumber || !paymentDate || !paymentType) {
      toast.error('Please provide a reference number, payment date, and payment type.');
      return;
    }
  
    try {
      const { error } = await supabase
        .from('invoices')
        .update({
          status: 'paid',
          reference_number: referenceNumber,
          payment_date: paymentDate,
          payment_type: paymentType,
        })
        .eq('invoice_number', selectedInvoice.invoice_number);
      if (error) throw new Error(`Failed to record payment: ${error.message}`);
  
      setInvoices(invoices.map(inv =>
        inv.invoice_number === selectedInvoice.invoice_number
          ? { ...inv, status: 'paid', reference_number: referenceNumber, payment_date: paymentDate, payment_type: paymentType }
          : inv
      ));
      setFilteredInvoices(filteredInvoices.map(inv =>
        inv.invoice_number === selectedInvoice.invoice_number
          ? { ...inv, status: 'paid', reference_number: referenceNumber, payment_date: paymentDate, payment_type: paymentType }
          : inv
      ));
  
      setIsPaymentModalOpen(false);
      setSelectedInvoice(null);
      setReferenceNumber('');
      setPaymentType('Check');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      toast.success('Payment recorded successfully!');
    } catch (err) {
      console.error('Error recording payment:', err);
      toast.error(`Failed to record payment: ${err.message}`);
    }
  };

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
    setSelectedPackage(null);
    setPackageSearchQuery('');
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setSelectedPackage(null);
    setPackageSearchQuery('');
  };

  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setIsPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedInvoice(null);
    setReferenceNumber('');
    setPaymentType('Check');
    setPaymentDate(new Date().toISOString().split('T')[0]);
  };

  // Bulk Payment Functions
  const openBulkPaymentModal = () => {
    setBulkPaymentSelectedInvoices([]);
    setBulkPaymentCustomerFilter('all');
    setBulkPaymentSearch('');
    setBulkPaymentType('Check');
    setBulkPaymentReference('');
    setBulkPaymentDate(new Date().toISOString().split('T')[0]);
    setIsBulkPaymentModalOpen(true);
  };

  const closeBulkPaymentModal = () => {
    setIsBulkPaymentModalOpen(false);
    setBulkPaymentSelectedInvoices([]);
    setBulkPaymentCustomerFilter('all');
    setBulkPaymentSearch('');
  };

  const toggleBulkPaymentInvoice = (invoiceNumber) => {
    setBulkPaymentSelectedInvoices(prev => {
      if (prev.includes(invoiceNumber)) {
        return prev.filter(num => num !== invoiceNumber);
      } else {
        return [...prev, invoiceNumber];
      }
    });
  };

  const toggleSelectAllBulkPayment = () => {
    const allFilteredNumbers = filteredUnpaidInvoices.map(inv => inv.invoice_number);
    const allSelected = allFilteredNumbers.every(num => bulkPaymentSelectedInvoices.includes(num));

    if (allSelected) {
      // Deselect all filtered
      setBulkPaymentSelectedInvoices(prev => prev.filter(num => !allFilteredNumbers.includes(num)));
    } else {
      // Select all filtered
      setBulkPaymentSelectedInvoices(prev => {
        const newSelection = [...prev];
        allFilteredNumbers.forEach(num => {
          if (!newSelection.includes(num)) {
            newSelection.push(num);
          }
        });
        return newSelection;
      });
    }
  };

  const handleBulkPayment = async () => {
    if (bulkPaymentSelectedInvoices.length === 0) {
      toast.error('Please select at least one invoice');
      return;
    }
    if (!bulkPaymentReference) {
      toast.error('Please enter a reference number');
      return;
    }

    setIsBulkPaymentProcessing(true);

    try {
      // Update all selected invoices
      const { error } = await supabase
        .from('invoices')
        .update({
          status: 'paid',
          reference_number: bulkPaymentReference,
          payment_date: bulkPaymentDate,
          payment_type: bulkPaymentType,
        })
        .in('invoice_number', bulkPaymentSelectedInvoices);

      if (error) throw new Error(`Failed to record payments: ${error.message}`);

      // Update local state
      setInvoices(prev => prev.map(inv =>
        bulkPaymentSelectedInvoices.includes(inv.invoice_number)
          ? { ...inv, status: 'paid', reference_number: bulkPaymentReference, payment_date: bulkPaymentDate, payment_type: bulkPaymentType }
          : inv
      ));
      setFilteredInvoices(prev => prev.map(inv =>
        bulkPaymentSelectedInvoices.includes(inv.invoice_number)
          ? { ...inv, status: 'paid', reference_number: bulkPaymentReference, payment_date: bulkPaymentDate, payment_type: bulkPaymentType }
          : inv
      ));

      toast.success(`Payment recorded for ${bulkPaymentSelectedInvoices.length} invoice(s)!`);
      closeBulkPaymentModal();
    } catch (err) {
      console.error('Error recording bulk payment:', err);
      toast.error(`Failed to record payments: ${err.message}`);
    } finally {
      setIsBulkPaymentProcessing(false);
    }
  };

  const openPdfModal = async (pdfPath) => {
    try {
      const { data, error } = await supabase.storage
        .from('invoices')
        .createSignedUrl(pdfPath, 300); // 300 seconds validity
      if (error) {
        console.error('Failed to generate signed URL:', error);
        throw new Error(`Failed to generate signed URL: ${error.message}`);
      }
  
      // Detect if running on Windows using navigator.platform
      const isWindows = navigator.platform.indexOf('Win') > -1;
  
      if (isWindows) {
        // On Windows: Open the PDF in a new tab...
        window.open(data.signedUrl, '_blank');
        // ...and refresh the original tab to clear any stuck modal state.
        window.location.reload();
      } else {
        // On non-Windows: Display the PDF in a modal.
        setSignedUrl(data.signedUrl);
        setCurrentPdfPath(pdfPath);
        setIsPdfModalOpen(true);
      }
    } catch (err) {
      console.error('Error opening PDF:', err);
      toast.error(`Failed to open PDF: ${err.message}`);
    }
  };
  

  const closePdfModal = () => {
    setIsPdfModalOpen(false);
    setSignedUrl(null);
    setPdfLoadError(false);
    setCurrentPdfPath(null);
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const openEditModal = (invoice) => {
    setSelectedInvoice(invoice);
    setEditReferenceNumber(invoice.reference_number || '');
    setEditPaymentType(invoice.payment_type || 'Check');
    setEditNotes(invoice.notes || '');
    setEditStatus(invoice.status || 'unpaid');
    setEditPaymentDate(invoice.payment_date ? new Date(invoice.payment_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
    setIsEditModalOpen(true);
  };
  
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedInvoice(null);
    setEditReferenceNumber('');
    setEditPaymentType('Check');
    setEditNotes('');
    setEditStatus('unpaid');
    setEditPaymentDate(new Date().toISOString().split('T')[0]);
  };
  
  const handleEditInvoice = async () => {
  if (!selectedInvoice) {
    toast.error('No invoice selected to edit.');
    return;
  }

  // Validate payment date if status is "Paid"
  if (editStatus === 'paid' && !editPaymentDate) {
    toast.error('Please provide a payment date for a paid invoice.');
    return;
  }

  try {
    // Update the invoice record
    const { error: updateError } = await supabase
      .from('invoices')
      .update({
        reference_number: editStatus === 'paid' ? editReferenceNumber || null : null,
        payment_type: editStatus === 'paid' ? editPaymentType || null : null,
        notes: editNotes || null,
        status: editStatus,
        payment_date: editStatus === 'paid' ? editPaymentDate : null,
      })
      .eq('invoice_number', selectedInvoice.invoice_number);
    if (updateError) throw new Error(`Failed to update invoice: ${updateError.message}`);

    // Update the local state
    setInvoices(invoices.map(inv =>
      inv.invoice_number === selectedInvoice.invoice_number
        ? { 
            ...inv, 
            reference_number: editStatus === 'paid' ? editReferenceNumber || null : null,
            payment_type: editStatus === 'paid' ? editPaymentType || null : null,
            notes: editNotes,
            status: editStatus,
            payment_date: editStatus === 'paid' ? editPaymentDate : null,
          }
        : inv
    ));
    setFilteredInvoices(filteredInvoices.map(inv =>
      inv.invoice_number === selectedInvoice.invoice_number
        ? { 
            ...inv, 
            reference_number: editStatus === 'paid' ? editReferenceNumber || null : null,
            payment_type: editStatus === 'paid' ? editPaymentType || null : null,
            notes: editNotes,
            status: editStatus,
            payment_date: editStatus === 'paid' ? editPaymentDate : null,
          }
        : inv
    ));

    closeEditModal();
    fetchData(); // Refetch data to ensure consistency
    toast.success('Invoice updated successfully!');
  } catch (err) {
    console.error('Error updating invoice:', err);
    toast.error(`Failed to update invoice: ${err.message}`);
  }
};

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice) {
      toast.error('No invoice selected to delete.');
      return;
    }
  
    try {
      // Delete the PDF file from storage
      const pdfFilePath = selectedInvoice.pdf_path;
      if (pdfFilePath) {
        const { error: storageError } = await supabase.storage
          .from('invoices')
          .remove([pdfFilePath]);
        if (storageError) throw new Error(`Failed to delete invoice PDF: ${storageError.message}`);
      }
  
      // Clear the invoice_number from associated deliveries
      const { error: deliveryError } = await supabase
        .from('deliveries')
        .update({ invoice_number: null })
        .eq('invoice_number', selectedInvoice.invoice_number);
      if (deliveryError) throw new Error(`Failed to clear invoice from deliveries: ${deliveryError.message}`);
  
      // Delete the invoice record
      const { error: deleteError } = await supabase
        .from('invoices')
        .delete()
        .eq('invoice_number', selectedInvoice.invoice_number);
      if (deleteError) throw new Error(`Failed to delete invoice: ${deleteError.message}`);
  
      // Update local state
      setInvoices(invoices.filter(inv => inv.invoice_number !== selectedInvoice.invoice_number));
      setFilteredInvoices(filteredInvoices.filter(inv => inv.invoice_number !== selectedInvoice.invoice_number));
  
      // Refetch packages to include the now-uninvoiced package
      const { data: deliveriesData, error: deliveriesError } = await supabase
        .from('deliveries')
        .select(`
          package_number, quantity, invoice_number, po_number, po_line_number, part_number,
          job_id, kanban_job_id,
          jobs!left(unit_price, customer_id, customers!left(*)),
          kanban_jobs!left(unit_price, description, customers!left(*)),
          parts!part_number(description)
        `);
      if (deliveriesError) throw new Error(`Deliveries fetch error: ${deliveriesError.message}`);
  
      const groupedPackages = deliveriesData.reduce((acc, delivery) => {
        const packageNumber = delivery.package_number;
        if (!acc[packageNumber]) {
          acc[packageNumber] = {
            package_number: packageNumber,
            po_number: delivery.po_number,
            deliveries: [],
            total_quantity: 0,
            total_amount: 0,
            customer: null,
            customer_nickname: '',
            has_invoice: false,
          };
        }
        if (delivery.jobs) {
          delivery.jobs.customers = delivery.jobs.customers || {};
        } else if (delivery.kanban_jobs) {
          delivery.kanban_jobs.customers = delivery.kanban_jobs.customers || {};
        }
        // Add description to the delivery object, prioritizing parts table
        delivery.description = delivery.parts?.description || delivery.kanban_jobs?.description || 'N/A';
        acc[packageNumber].deliveries.push(delivery);
        acc[packageNumber].total_quantity += delivery.quantity;
  
        const unitPrice = delivery.jobs?.unit_price || delivery.kanban_jobs?.unit_price || 0;
        acc[packageNumber].total_amount += delivery.quantity * unitPrice;
  
        if (!acc[packageNumber].customer) {
          acc[packageNumber].customer = delivery.jobs?.customers || delivery.kanban_jobs?.customers || {};
          acc[packageNumber].customer_nickname = acc[packageNumber].customer.nickname || '-';
        }
  
        if (delivery.invoice_number) {
          acc[packageNumber].has_invoice = true;
        }
  
        return acc;
      }, {});
  
      const packageArray = Object.values(groupedPackages).filter(pkg => !pkg.has_invoice);
      setPackages(packageArray);
      setFilteredPackages(packageArray);
  
      closeEditModal();
      toast.success(`Invoice ${selectedInvoice.invoice_number} deleted successfully!`);
    } catch (err) {
      console.error('Error deleting invoice:', err);
      toast.error(`Failed to delete invoice: ${err.message}`);
    }
  };


const toggleRow = (invoiceNumber) => {
    setExpandedRows((prev) => ({
      ...prev,
      [invoiceNumber]: !prev[invoiceNumber],
    }));
  };

const handleSendEmail = async () => {
  if (!selectedInvoiceForEmail || emailRecipients.length === 0) {
    toast.error('Please select an invoice and enter at least one valid email address.');
    return;
  }

  setIsEmailLoading(true); // Start loading state
  setEmailSuccess(false); // Reset success state

  try {
    // Fetch the invoice PDF from Supabase Storage
    const { data: pdfData, error: pdfError } = await supabase.storage
      .from('invoices')
      .download(selectedInvoiceForEmail.pdf_path);
    if (pdfError) throw new Error(`Failed to download invoice PDF: ${pdfError.message}`);

    // Convert the PDF blob to a base64 string using FileReader
    const pdfBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = () => reject(new Error('Failed to read PDF blob as base64'));
      reader.readAsDataURL(pdfData);
    });

    // Compress the PDF base64 to reduce payload size
    console.log(`Original PDF size: ${pdfBase64.length} characters`);
    
    const startTime = Date.now();
    toast.info('Compressing PDF...', { autoClose: false, toastId: 'compressing' });
    
    const compressedPdf = await compressBase64(pdfBase64, 8000); // 8 second timeout
    
    const compressionTime = Date.now() - startTime;
    console.log(`Compressed PDF size: ${compressedPdf.length} characters`);
    console.log(`Compression took: ${compressionTime}ms`);
    console.log(`Compression ratio: ${(compressedPdf.length / pdfBase64.length * 100).toFixed(1)}%`);
    
    toast.dismiss('compressing');
    
    if (compressedPdf.length < pdfBase64.length) {
      toast.success(`PDF compressed by ${(100 - (compressedPdf.length / pdfBase64.length * 100)).toFixed(0)}%`);
    } else {
      toast.info('PDF sent uncompressed (compression not beneficial)');
    }

    // Call the Supabase Edge Function to send the email
    const response = await fetch('https://eiojxadvwbyjehiowyhd.supabase.co/functions/v1/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        invoiceNumber: selectedInvoiceForEmail.invoice_number,
        totalAmount: selectedInvoiceForEmail.total_amount,
        dueDate: selectedInvoiceForEmail.net_due_date,
        recipient: emailRecipients, // Send the array of recipients
        pdfBase64: compressedPdf, // Use compressed PDF instead of original
      }),
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to send email');
    }

    // Log the successful email send to the emails table
    const { data: newEmailLog, error: logError } = await supabase
      .from('emails')
      .insert({
        invoice_number: selectedInvoiceForEmail.invoice_number,
        recipients: emailRecipients,
        status: 'sent',
        error_message: null,
      })
      .select()
      .single();
    if (logError) {
      console.error('Error logging email send:', logError);
      toast.warn('Email sent, but failed to log the send event.');
    } else {
      // Add the new email log to the state
      setEmailLogs(prev => [newEmailLog, ...prev]);
      // Update the invoices state with the new email log
      setInvoices(prev =>
        prev.map(inv =>
          inv.invoice_number === selectedInvoiceForEmail.invoice_number
            ? { ...inv, email_logs: [newEmailLog, ...(inv.email_logs || [])] }
            : inv
        )
      );
      setFilteredInvoices(prev =>
        prev.map(inv =>
          inv.invoice_number === selectedInvoiceForEmail.invoice_number
            ? { ...inv, email_logs: [newEmailLog, ...(inv.email_logs || [])] }
            : inv
        )
      );
    }

    setIsEmailLoading(false); // Stop loading state
    setEmailSuccess(true); // Set success state

    // Show success message for 2 seconds, then close the modal
    setTimeout(() => {
      setIsEmailModalOpen(false);
      setEmailRecipients([]);
      setCurrentEmailInput('');
      setEmailSuccess(false);
      setSelectedInvoiceForEmail(null);
    }, 2000);

  } catch (err) {
    console.error('Error sending email:', err);

    // Log the failed email send to the emails table
    const { data: failedEmailLog, error: logError } = await supabase
      .from('emails')
      .insert({
        invoice_number: selectedInvoiceForEmail?.invoice_number || 'unknown',
        recipients: emailRecipients,
        status: 'failed',
        error_message: err.message,
      })
      .select()
      .single();
    if (logError) {
      console.error('Error logging email failure:', logError);
    } else {
      // Add the failed email log to the state
      setEmailLogs(prev => [failedEmailLog, ...prev]);
      setInvoices(prev =>
        prev.map(inv =>
          inv.invoice_number === selectedInvoiceForEmail?.invoice_number
            ? { ...inv, email_logs: [failedEmailLog, ...(inv.email_logs || [])] }
            : inv
        )
      );
      setFilteredInvoices(prev =>
        prev.map(inv =>
          inv.invoice_number === selectedInvoiceForEmail?.invoice_number
            ? { ...inv, email_logs: [failedEmailLog, ...(inv.email_logs || [])] }
            : inv
        )
      );
    }

    toast.error(`Failed to send email: ${err.message}`);
    setIsEmailLoading(false); // Stop loading state on error
  }
};

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-slate-50 text-slate-500">Loading...</div>;

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 h-12 px-4 flex items-center">
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-slate-800">Invoices</h1>
            <span className="text-xs text-slate-500">{filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Date Filter */}
            <div className="relative">
              <button
                onClick={() => setShowDateDropdown(!showDateDropdown)}
                onBlur={() => setTimeout(() => setShowDateDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[120px] justify-between"
              >
                <span className="text-slate-800">{dateFilterOptions.find(o => o.value === dateFilter)?.label}</span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showDateDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[130px]">
                  {dateFilterOptions.map(option => (
                    <div
                      key={option.value}
                      onClick={() => { setDateFilter(option.value); setShowDateDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${dateFilter === option.value ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Customer Filter */}
            <div className="relative">
              <button
                onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[130px] justify-between"
              >
                <span className={filterCustomer ? 'text-slate-800' : 'text-slate-500'}>
                  {filterCustomer ? customers.find(c => c.id === parseInt(filterCustomer))?.nickname || 'Customer' : 'All Customers'}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showCustomerDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[150px] max-h-64 overflow-y-auto">
                  <div
                    onClick={() => { setFilterCustomer(''); setShowCustomerDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!filterCustomer ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    All Customers
                  </div>
                  {customers.map(customer => (
                    <div
                      key={customer.id}
                      onClick={() => { setFilterCustomer(String(customer.id)); setShowCustomerDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterCustomer === String(customer.id) ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {customer.nickname}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Status Filter */}
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                onBlur={() => setTimeout(() => setShowStatusDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[110px] justify-between"
              >
                <span className="text-slate-800">{statusFilterOptions.find(o => o.value === filterStatus)?.label}</span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showStatusDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[120px]">
                  {statusFilterOptions.map(option => (
                    <div
                      key={option.value}
                      onClick={() => { setFilterStatus(option.value); setShowStatusDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterStatus === option.value ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Export */}
            <button
              onClick={openExportModal}
              className="px-3 py-1.5 text-sm bg-white text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
            >
              Export
            </button>

            {/* Bulk Payment */}
            {unpaidInvoices.length > 0 && (
              <button
                onClick={openBulkPaymentModal}
                className="px-3 py-1.5 text-sm bg-white text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
              >
                Bulk Payment ({unpaidInvoices.length})
              </button>
            )}

            {/* Bulk Send Emails */}
            {unsentInvoices.length > 0 && (
              <button
                onClick={openBulkEmailModal}
                className="px-3 py-1.5 text-sm bg-white text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
              >
                Bulk Send ({unsentInvoices.length})
              </button>
            )}

            {/* Create Invoice */}
            <button
              onClick={openCreateModal}
              className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
            >
              + Create Invoice
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="px-4 py-3 bg-white border-b border-slate-200">
        <div className="flex items-center gap-6">
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">A/R Balance</span>
            <p className="text-xl font-semibold text-red-600 tabular-nums">
              ${accountsReceivable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Cash Received</span>
            <p className="text-xl font-semibold text-green-600 tabular-nums">
              ${cashReceived.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Total Invoiced</span>
            <p className="text-xl font-semibold text-slate-800 tabular-nums">
              ${totalInvoiced.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Overdue</span>
            <p className="text-xl font-semibold text-amber-600 tabular-nums">
              ${overdueAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white border-b border-slate-200">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide w-8"></th>
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                onClick={() => handleSort('invoice_number')}
              >
                <div className="flex items-center gap-1">
                  Invoice {sortField === 'invoice_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                </div>
              </th>
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                onClick={() => handleSort('customer_nickname')}
              >
                <div className="flex items-center gap-1">
                  Customer {sortField === 'customer_nickname' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                </div>
              </th>
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                onClick={() => handleSort('total_amount')}
              >
                <div className="flex items-center gap-1">
                  Total {sortField === 'total_amount' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                </div>
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</th>
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                onClick={() => handleSort('net_due_date')}
              >
                <div className="flex items-center gap-1">
                  Due {sortField === 'net_due_date' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                </div>
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Payment</th>
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-1">
                  Created {sortField === 'created_at' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                </div>
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Sent</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((inv) => {
              const isOverdue = inv.status === 'unpaid' && new Date(inv.net_due_date) < new Date();
              const isExpanded = expandedRows[inv.invoice_number] || false;
              const hasEmails = inv.email_logs && inv.email_logs.length > 0;
              return (
                <Fragment key={inv.invoice_number}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <button
                        onClick={() => toggleRow(inv.invoice_number)}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronUpIcon className="w-4 h-4" />
                        ) : (
                          <ChevronDownIcon className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span className="text-slate-700 font-medium">{inv.invoice_number}</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{inv.customer_nickname}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-800 font-medium tabular-nums">${inv.total_amount.toFixed(2)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${
                        inv.status === 'paid'
                          ? 'bg-green-100 text-green-700'
                          : isOverdue
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                      }`}>
                        {isOverdue ? 'Overdue' : inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">
                      <span className={isOverdue ? 'text-amber-600 font-medium' : ''}>
                        {new Date(inv.net_due_date).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">
                      {inv.status === 'paid' ? (
                        <span className="text-xs">
                          {inv.payment_type} #{inv.reference_number}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{new Date(inv.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      {hasEmails ? (
                        <button
                          onClick={() => {
                            setSelectedEmailHistory(inv.email_logs);
                            setIsEmailHistoryModalOpen(true);
                          }}
                          className="text-green-600 hover:text-green-700"
                          title="View email history"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => openPdfModal(inv.pdf_path)}
                          className="text-slate-500 hover:text-slate-700 hover:underline transition-colors"
                        >
                          View
                        </button>
                        <button
                          onClick={() => openEditModal(inv)}
                          className="text-slate-500 hover:text-slate-700 hover:underline transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInvoiceForEmail(inv);
                            // Pre-populate recipients with sales email + billing emails
                            const initialRecipients = [SALES_EMAIL];
                            if (inv.billing_contact_emails && inv.billing_contact_emails.length > 0) {
                              inv.billing_contact_emails.forEach(email => {
                                if (email && !initialRecipients.includes(email.trim())) {
                                  initialRecipients.push(email.trim());
                                }
                              });
                            } else if (inv.billing_contact_email) {
                              const email = inv.billing_contact_email.trim();
                              if (!initialRecipients.includes(email)) {
                                initialRecipients.push(email);
                              }
                            }
                            setEmailRecipients(initialRecipients);
                            setIsEmailModalOpen(true);
                          }}
                          className="text-slate-500 hover:text-slate-700 hover:underline transition-colors"
                        >
                          Email
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan="10" className="p-3 bg-slate-50">
                        {/* Package/PO Info */}
                        <div className="flex items-center gap-6 mb-3 text-sm">
                          <div>
                            <span className="text-slate-500">Package:</span>{' '}
                            <span className="font-medium text-slate-700">{inv.package_number}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">PO #:</span>{' '}
                            <span className="font-medium text-slate-700">{inv.po_number}</span>
                          </div>
                        </div>
                        {/* Line Items Table */}
                        <div className="overflow-x-auto rounded-sm border border-slate-200">
                          <table className="w-full">
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Line</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Part #</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Description</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Unit Price</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Total</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white">
                              {inv.deliveries.length > 0 ? (
                                inv.deliveries.map((delivery, index) => (
                                  <tr key={index} className="border-b border-slate-100">
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{delivery.po_line_number || '-'}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{delivery.part_number || '-'}</td>
                                    <td className="px-3 py-2 text-sm text-slate-600">{delivery.description || '-'}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{delivery.quantity || '-'}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">${(delivery.jobs?.unit_price || delivery.kanban_jobs?.unit_price || 0).toFixed(2)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">${(delivery.quantity * (delivery.jobs?.unit_price || delivery.kanban_jobs?.unit_price || 0)).toFixed(2)}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="6" className="px-3 py-4 text-sm text-slate-500 text-center">
                                    No line items available for this package.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

        {/* Create Invoice Modal */}
        <Transition appear show={isCreateModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeCreateModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-sm bg-white text-left shadow-xl transition-all">
                    <div className="border-b border-slate-200 px-4 py-3">
                      <Dialog.Title as="h2" className="text-lg font-semibold text-slate-800">
                        {isBatchMode ? 'Creating Invoices...' : 'Create Invoice'}
                      </Dialog.Title>
                    </div>

                    {/* Batch Mode: Progress View */}
                    {isBatchMode ? (
                      <div className="p-4">
                        {/* Progress Header */}
                        <div className="mb-4">
                          {!isBatchComplete ? (
                            <div className="flex items-center gap-3">
                              <svg className="animate-spin h-5 w-5 text-slate-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="text-sm text-slate-600">
                                Creating invoice {batchProgress} of {batchTotal}...
                              </span>
                            </div>
                          ) : batchWasCancelled ? (
                            <div className="flex items-center gap-3">
                              <svg className="h-5 w-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <span className="text-sm font-medium text-slate-700">
                                Stopped. {batchResults.filter(r => r.success).length} created, {batchTotal - batchProgress} skipped
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3">
                              <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-sm font-medium text-slate-700">
                                Complete! {batchResults.filter(r => r.success).length} created, {batchResults.filter(r => !r.success).length} failed
                              </span>
                            </div>
                          )}
                          {/* Progress Bar */}
                          <div className="mt-3 w-full bg-slate-200 rounded-full h-2">
                            <div
                              className="bg-slate-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${(batchProgress / batchTotal) * 100}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Results List */}
                        <div className="border border-slate-200 rounded-sm max-h-80 overflow-y-auto">
                          <table className="w-full">
                            <thead className="bg-slate-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-8"></th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Invoice #</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Package</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Customer</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {batchResults.map((result, index) => (
                                <tr key={index} className="border-b border-slate-100">
                                  <td className="px-3 py-2">
                                    {result.success ? (
                                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-slate-700 font-medium">{result.invoice_number}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600">{result.package_number}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600">{result.customer}</td>
                                  <td className="px-3 py-2 text-sm">
                                    {result.success ? (
                                      <span className="text-green-600">Created</span>
                                    ) : (
                                      <span className="text-red-500">{result.error}</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      /* Normal Mode: Package Selection */
                      <div className="p-4 space-y-4">
                        {/* Search */}
                        <div>
                          <input
                            type="text"
                            value={packageSearchQuery}
                            onChange={(e) => setPackageSearchQuery(e.target.value)}
                            placeholder="Search by package number..."
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          />
                        </div>
                        <div className="overflow-x-auto max-h-72 border border-slate-200 rounded-sm">
                          <table className="w-full">
                            <thead className="bg-slate-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Package</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">PO #</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Customer</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Ship Date</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Qty</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Amount</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredPackages.map((pkg) => (
                                <tr key={pkg.package_number} className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${selectedPackage?.package_number === pkg.package_number ? 'bg-slate-100' : ''}`} onClick={() => setSelectedPackage(pkg)}>
                                  <td className="px-3 py-2 text-sm text-slate-700 font-medium">{pkg.package_number}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600">{pkg.po_number}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600">{pkg.customer_nickname}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600">
                                    {pkg.deliveries[0]?.ship_date ? new Date(pkg.deliveries[0].ship_date).toLocaleDateString() : '-'}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{pkg.total_quantity}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">${pkg.total_amount.toFixed(2)}</td>
                                  <td className="px-3 py-2 text-sm">
                                    {selectedPackage?.package_number === pkg.package_number && (
                                      <span className="text-green-600">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                        </svg>
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Package Preview */}
                        {selectedPackage && (
                          <div className="border border-slate-200 rounded-sm p-4 bg-slate-50">
                            <h3 className="text-sm font-semibold text-slate-800 mb-3">Package {selectedPackage.package_number}</h3>
                            <div className="flex gap-6 text-sm mb-3">
                              <span className="text-slate-600"><span className="font-medium text-slate-700">PO:</span> {selectedPackage.po_number}</span>
                              <span className="text-slate-600"><span className="font-medium text-slate-700">Customer:</span> {selectedPackage.customer.nickname}</span>
                            </div>
                            <div className="overflow-x-auto border border-slate-200 rounded-sm">
                              <table className="w-full bg-white">
                                <thead className="bg-slate-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Line</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Part #</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Qty</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Unit Price</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedPackage.deliveries.map((delivery, index) => (
                                    <tr key={index} className="border-b border-slate-100">
                                      <td className="px-3 py-2 text-sm text-slate-600">{delivery.po_line_number}</td>
                                      <td className="px-3 py-2 text-sm text-slate-600">{delivery.part_number}</td>
                                      <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{delivery.quantity}</td>
                                      <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">${(delivery.jobs?.unit_price || delivery.kanban_jobs?.unit_price || 0).toFixed(2)}</td>
                                      <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">${(delivery.quantity * (delivery.jobs?.unit_price || delivery.kanban_jobs?.unit_price || 0)).toFixed(2)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className="mt-3 text-sm text-right font-semibold text-slate-800">
                              Total: ${selectedPackage.total_amount.toFixed(2)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="border-t border-slate-200 px-4 py-3 flex justify-between">
                      <div>
                        {!isBatchMode && filteredPackages.length > 1 && (
                          <button
                            onClick={handleCreateAllInvoices}
                            className="px-4 py-2 text-sm bg-slate-100 text-slate-700 border border-slate-300 rounded-sm hover:bg-slate-200 transition-colors"
                          >
                            Create All ({filteredPackages.length})
                          </button>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {isBatchMode ? (
                          isBatchComplete ? (
                            <button
                              onClick={closeBatchMode}
                              className="px-4 py-2 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                            >
                              Done
                            </button>
                          ) : (
                            <button
                              onClick={handleCancelBatch}
                              className="px-4 py-2 text-sm text-red-600 border border-red-300 rounded-sm hover:bg-red-50 transition-colors"
                            >
                              Stop
                            </button>
                          )
                        ) : (
                          <>
                            <button
                              onClick={closeCreateModal}
                              className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleCreateInvoice}
                              className="px-4 py-2 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                              disabled={!selectedPackage}
                            >
                              Create Invoice
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Record Payment Modal */}
        <Transition appear show={isPaymentModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closePaymentModal}>
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="border-b border-slate-200 px-4 py-3">
                      <Dialog.Title as="h2" className="text-lg font-semibold text-slate-800">
                        Record Payment - {selectedInvoice?.invoice_number}
                      </Dialog.Title>
                    </div>
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Payment Type</label>
                        <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400">
                          <option value="Check">Check</option>
                          <option value="Credit Card">Credit Card</option>
                          <option value="ACH">ACH</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Reference Number</label>
                        <input type="text" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400" placeholder="Enter reference number" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date</label>
                        <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400" />
                      </div>
                    </div>
                    <div className="border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
                      <button onClick={closePaymentModal} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors">Cancel</button>
                      <button onClick={handleRecordPayment} className="px-4 py-2 text-sm bg-green-600 text-white rounded-sm hover:bg-green-700 transition-colors">Record Payment</button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Bulk Payment Modal */}
        <Transition appear show={isBulkPaymentModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => !isBulkPaymentProcessing && closeBulkPaymentModal()}>
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="border-b border-slate-200 px-4 py-3">
                      <Dialog.Title as="h2" className="text-lg font-semibold text-slate-800">
                        Bulk Record Payment
                      </Dialog.Title>
                    </div>
                    <div className="p-4 space-y-4">
                      {/* Filters Row */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Filter by Customer</label>
                          <select
                            value={bulkPaymentCustomerFilter}
                            onChange={(e) => setBulkPaymentCustomerFilter(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          >
                            <option value="all">All Customers</option>
                            {[...new Set(unpaidInvoices.map(inv => inv.customer_nickname))].sort().map(customer => (
                              <option key={customer} value={customer}>{customer}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Search Invoice #</label>
                          <input
                            type="text"
                            value={bulkPaymentSearch}
                            onChange={(e) => setBulkPaymentSearch(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            placeholder="Search by invoice number..."
                          />
                        </div>
                      </div>

                      {/* Invoice Selection Table */}
                      <div className="border border-slate-200 rounded-sm overflow-hidden">
                        <div className="max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left">
                                  <input
                                    type="checkbox"
                                    checked={filteredUnpaidInvoices.length > 0 && filteredUnpaidInvoices.every(inv => bulkPaymentSelectedInvoices.includes(inv.invoice_number))}
                                    onChange={toggleSelectAllBulkPayment}
                                    className="rounded-sm border-slate-300"
                                  />
                                </th>
                                <th className="px-3 py-2 text-left text-slate-600 font-medium">Invoice #</th>
                                <th className="px-3 py-2 text-left text-slate-600 font-medium">Customer</th>
                                <th className="px-3 py-2 text-right text-slate-600 font-medium">Amount</th>
                                <th className="px-3 py-2 text-left text-slate-600 font-medium">Due Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredUnpaidInvoices.length === 0 ? (
                                <tr>
                                  <td colSpan="5" className="px-3 py-4 text-center text-slate-500">
                                    No unpaid invoices found
                                  </td>
                                </tr>
                              ) : (
                                filteredUnpaidInvoices.map(inv => (
                                  <tr
                                    key={inv.invoice_number}
                                    className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${
                                      bulkPaymentSelectedInvoices.includes(inv.invoice_number) ? 'bg-slate-50' : ''
                                    }`}
                                    onClick={() => toggleBulkPaymentInvoice(inv.invoice_number)}
                                  >
                                    <td className="px-3 py-2">
                                      <input
                                        type="checkbox"
                                        checked={bulkPaymentSelectedInvoices.includes(inv.invoice_number)}
                                        onChange={() => toggleBulkPaymentInvoice(inv.invoice_number)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="rounded-sm border-slate-300"
                                      />
                                    </td>
                                    <td className="px-3 py-2 font-medium text-slate-800">{inv.invoice_number}</td>
                                    <td className="px-3 py-2 text-slate-600">{inv.customer_nickname}</td>
                                    <td className="px-3 py-2 text-right text-slate-800 tabular-nums">
                                      ${inv.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-3 py-2 text-slate-600">
                                      {inv.net_due_date ? new Date(inv.net_due_date).toLocaleDateString() : '-'}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Selected Summary */}
                      <div className="flex items-center justify-between text-sm bg-slate-50 px-3 py-2 rounded-sm">
                        <span className="text-slate-600">
                          Selected: <span className="font-medium text-slate-800">{bulkPaymentSelectedInvoices.length} invoice(s)</span>
                        </span>
                        <span className="text-slate-600">
                          Total: <span className="font-semibold text-slate-800">
                            ${unpaidInvoices
                              .filter(inv => bulkPaymentSelectedInvoices.includes(inv.invoice_number))
                              .reduce((sum, inv) => sum + (inv.total_amount || 0), 0)
                              .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </span>
                      </div>

                      {/* Payment Details */}
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Payment Type</label>
                          <select
                            value={bulkPaymentType}
                            onChange={(e) => setBulkPaymentType(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          >
                            <option value="Check">Check</option>
                            <option value="Credit Card">Credit Card</option>
                            <option value="ACH">ACH</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Reference Number</label>
                          <input
                            type="text"
                            value={bulkPaymentReference}
                            onChange={(e) => setBulkPaymentReference(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            placeholder="Check # or ref"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date</label>
                          <input
                            type="date"
                            value={bulkPaymentDate}
                            onChange={(e) => setBulkPaymentDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
                      <button
                        onClick={closeBulkPaymentModal}
                        className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
                        disabled={isBulkPaymentProcessing}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleBulkPayment}
                        className="px-4 py-2 text-sm bg-green-600 text-white rounded-sm hover:bg-green-700 transition-colors disabled:bg-slate-300 flex items-center"
                        disabled={isBulkPaymentProcessing || bulkPaymentSelectedInvoices.length === 0 || !bulkPaymentReference}
                      >
                        {isBulkPaymentProcessing ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Recording...
                          </>
                        ) : (
                          `Record Payment (${bulkPaymentSelectedInvoices.length})`
                        )}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* PDF Viewer Modal */}
        <Transition appear show={isPdfModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closePdfModal}>
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-[90vw] h-[90vh] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] transform overflow-hidden rounded-sm bg-white shadow-xl transition-all flex flex-col">
                    <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                      <Dialog.Title as="h2" className="text-lg font-semibold text-slate-800">Invoice Preview</Dialog.Title>
                      <button onClick={closePdfModal} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                    <div className="flex-1 p-4 overflow-auto">
                      {isPdfLoading ? (
                        <p className="text-slate-500">Loading PDF...</p>
                      ) : pdfLoadError ? (
                        <p className="text-slate-500">Unable to load PDF. It has been opened in a new tab.</p>
                      ) : signedUrl ? (
                        <iframe src={signedUrl} className="w-full h-full border border-slate-200 rounded-sm" title="Invoice Preview" onError={async () => {
                          setPdfLoadError(true);
                          toast.error('Failed to load PDF in preview. Opening in a new tab instead...');
                          try {
                            const { data: signedData, error: signedError } = await supabase.storage.from('invoices').createSignedUrl(currentPdfPath, 300);
                            if (signedError) throw new Error(`Fallback signed URL failed: ${signedError.message}`);
                            window.open(signedData.signedUrl, '_blank');
                          } catch (signedErr) {
                            console.error('Error in iframe fallback signed URL:', signedErr);
                            toast.error(`Failed to open PDF in new tab: ${signedErr.message}`);
                          }
                        }} />
                      ) : (
                        <p className="text-slate-500">Unable to load PDF.</p>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Edit Invoice Modal */}
        <Transition appear show={isEditModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeEditModal}>
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="border-b border-slate-200 px-4 py-3">
                      <Dialog.Title as="h2" className="text-lg font-semibold text-slate-800">Edit Invoice - {selectedInvoice?.invoice_number}</Dialog.Title>
                    </div>
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                        <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400">
                          <option value="unpaid">Unpaid</option>
                          <option value="paid">Paid</option>
                        </select>
                      </div>
                      {editStatus === 'paid' && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date</label>
                          <input type="date" value={editPaymentDate} onChange={(e) => setEditPaymentDate(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400" />
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Payment Type</label>
                        <select value={editPaymentType} onChange={(e) => setEditPaymentType(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400">
                          <option value="Check">Check</option>
                          <option value="Credit Card">Credit Card</option>
                          <option value="ACH">ACH</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Reference Number</label>
                        <input type="text" value={editReferenceNumber} onChange={(e) => setEditReferenceNumber(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400" placeholder="Enter reference number" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                        <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400" placeholder="Enter notes (optional)" rows="3" />
                      </div>
                    </div>
                    <div className="border-t border-slate-200 px-4 py-3 flex justify-between">
                      <button onClick={handleDeleteInvoice} className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-sm hover:bg-red-50 transition-colors">Delete</button>
                      <div className="flex gap-2">
                        <button onClick={closeEditModal} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors">Cancel</button>
                        <button onClick={handleEditInvoice} className="px-4 py-2 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors">Save Changes</button>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Bulk Email Modal */}
        <Transition appear show={isBulkEmailModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => { if (!isBulkEmailMode || isBulkEmailComplete) closeBulkEmailModal(); }}>
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="border-b border-slate-200 px-4 py-3">
                      <Dialog.Title as="h2" className="text-lg font-semibold text-slate-800">
                        {isBulkEmailMode ? 'Sending Emails...' : 'Bulk Send Invoices'}
                      </Dialog.Title>
                    </div>

                    {/* Bulk Email Mode: Progress View */}
                    {isBulkEmailMode ? (
                      <div className="p-4">
                        {/* Progress Header */}
                        <div className="mb-4">
                          {!isBulkEmailComplete ? (
                            <div className="flex items-center gap-3">
                              <svg className="animate-spin h-5 w-5 text-slate-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="text-sm text-slate-600">
                                Sending email {bulkEmailProgress} of {bulkEmailTotal}...
                              </span>
                            </div>
                          ) : bulkEmailWasCancelled ? (
                            <div className="flex items-center gap-3">
                              <svg className="h-5 w-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <span className="text-sm font-medium text-slate-700">
                                Stopped. {bulkEmailResults.filter(r => r.success).length} sent, {bulkEmailTotal - bulkEmailProgress} skipped
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3">
                              <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-sm font-medium text-slate-700">
                                Complete! {bulkEmailResults.filter(r => r.success).length} sent, {bulkEmailResults.filter(r => !r.success).length} failed
                              </span>
                            </div>
                          )}
                          {/* Progress Bar */}
                          <div className="mt-3 w-full bg-slate-200 rounded-full h-2">
                            <div
                              className="bg-slate-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${(bulkEmailProgress / bulkEmailTotal) * 100}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Results List */}
                        <div className="border border-slate-200 rounded-sm max-h-80 overflow-y-auto">
                          <table className="w-full">
                            <thead className="bg-slate-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase w-8"></th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Invoice</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Customer</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Recipient</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bulkEmailResults.map((result, index) => (
                                <tr key={index} className="border-b border-slate-100">
                                  <td className="px-3 py-2">
                                    {result.success ? (
                                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-slate-700 font-medium">{result.invoice_number}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600">{result.customer}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600">{result.recipients.join(', ') || '-'}</td>
                                  <td className="px-3 py-2 text-sm">
                                    {result.success ? (
                                      <span className="text-green-600">Sent</span>
                                    ) : (
                                      <span className="text-red-500">{result.error}</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      /* Normal Mode: Invoice List */
                      <div className="p-4">
                        <p className="text-sm text-slate-600 mb-4">
                          The following {unsentInvoices.length} invoice(s) have not been emailed yet. Each will be sent to the customer's billing email.
                        </p>
                        <div className="border border-slate-200 rounded-sm max-h-80 overflow-y-auto">
                          <table className="w-full">
                            <thead className="bg-slate-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Invoice</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Customer</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Amount</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Recipient</th>
                              </tr>
                            </thead>
                            <tbody>
                              {unsentInvoices.map((inv) => (
                                <tr key={inv.invoice_number} className="border-b border-slate-100">
                                  <td className="px-3 py-2 text-sm text-slate-700 font-medium">{inv.invoice_number}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600">{inv.customer_nickname}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">${inv.total_amount.toFixed(2)}</td>
                                  <td className="px-3 py-2 text-sm text-slate-600">
                                    {inv.billing_contact_emails?.length > 0
                                      ? inv.billing_contact_emails.join(', ')
                                      : inv.billing_contact_email || inv.shipping_contact_email || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
                      {isBulkEmailMode ? (
                        isBulkEmailComplete ? (
                          <button
                            onClick={closeBulkEmailModal}
                            className="px-4 py-2 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors"
                          >
                            Done
                          </button>
                        ) : (
                          <button
                            onClick={handleCancelBulkEmail}
                            className="px-4 py-2 text-sm text-red-600 border border-red-300 rounded-sm hover:bg-red-50 transition-colors"
                          >
                            Stop
                          </button>
                        )
                      ) : (
                        <>
                          <button
                            onClick={closeBulkEmailModal}
                            className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleBulkSendEmails}
                            disabled={unsentInvoices.length === 0}
                            className="px-4 py-2 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                          >
                            Send All ({unsentInvoices.length})
                          </button>
                        </>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Email History Modal */}
        <Transition appear show={isEmailHistoryModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setIsEmailHistoryModalOpen(false)}>
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="border-b border-slate-200 px-4 py-3">
                      <Dialog.Title as="h2" className="text-lg font-semibold text-slate-800">Email History - {selectedEmailHistory[0]?.invoice_number}</Dialog.Title>
                    </div>
                    <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                      {selectedEmailHistory.length > 0 ? (
                        selectedEmailHistory.map((log) => (
                          <div key={log.id} className="p-3 border border-slate-200 rounded-sm bg-slate-50">
                            <p className="text-sm text-slate-600"><span className="font-medium text-slate-700">Sent:</span> {new Date(log.sent_at).toLocaleString()}</p>
                            <p className="text-sm text-slate-600"><span className="font-medium text-slate-700">To:</span> {log.recipients.join(', ')}</p>
                            <p className="text-sm text-slate-600"><span className="font-medium text-slate-700">Status:</span>{' '}
                              <span className={log.status === 'sent' ? 'text-green-600' : 'text-red-600'}>
                        {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                              </span>
                            </p>
                            {log.error_message && <p className="text-sm text-red-600"><span className="font-medium">Error:</span> {log.error_message}</p>}
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500 text-sm">No email history available.</p>
                      )}
                    </div>
                    <div className="border-t border-slate-200 px-4 py-3 flex justify-end">
                      <button onClick={() => setIsEmailHistoryModalOpen(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors">Close</button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Send Email Modal */}
        <Transition appear show={isEmailModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => { if (!isEmailLoading) { setIsEmailModalOpen(false); setEmailRecipients([]); setCurrentEmailInput(''); setEmailSuccess(false); setSelectedInvoiceForEmail(null); } }}>
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="border-b border-slate-200 px-4 py-3">
                      <Dialog.Title as="h2" className="text-lg font-semibold text-slate-800">Send Invoice - {selectedInvoiceForEmail?.invoice_number}</Dialog.Title>
                    </div>
                    <div className="p-4">
                      {emailSuccess ? (
                        <div className="py-8 text-center">
                          <svg className="w-12 h-12 text-green-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <p className="text-green-600 font-medium">Email sent successfully!</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Recipients</label>
                            <div className="flex flex-wrap gap-2 p-2 border border-slate-200 rounded-sm focus-within:ring-1 focus-within:ring-slate-400 min-h-[42px]">
                              {emailRecipients.map((email, index) => (
                                <div key={index} className="flex items-center bg-slate-100 text-slate-700 text-sm px-2 py-1 rounded-sm">
                                  {email}
                                  <button onClick={() => setEmailRecipients(emailRecipients.filter((_, i) => i !== index))} className="ml-2 text-slate-500 hover:text-slate-700">×</button>
                                </div>
                              ))}
                              <input
                                type="text"
                                value={currentEmailInput}
                                onChange={(e) => setCurrentEmailInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if ((e.key === ',' || e.key === 'Enter' || e.key === 'Tab' || e.key === ' ') && currentEmailInput.trim()) {
                                    e.preventDefault();
                                    const email = currentEmailInput.trim();
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                    if (emailRegex.test(email) && !emailRecipients.includes(email)) {
                                      setEmailRecipients([...emailRecipients, email]);
                                      setCurrentEmailInput('');
                                    } else {
                                      toast.error('Invalid or duplicate email address');
                                    }
                                  }
                                }}
                                className="flex-1 min-w-[150px] p-1 text-sm border-none focus:outline-none"
                                placeholder={emailRecipients.length === 0 ? "Enter email addresses..." : "Add another..."}
                                disabled={isEmailLoading}
                              />
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-700 mb-1">Quick Add</p>
                            <div className="flex flex-wrap gap-2">
                              {/* Show all billing emails from array */}
                              {selectedInvoiceForEmail?.billing_contact_emails?.length > 0 ? (
                                selectedInvoiceForEmail.billing_contact_emails.map((email, index) => (
                                  <button
                                    key={index}
                                    onClick={() => {
                                      const emailTrimmed = email.trim();
                                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                      if (emailRegex.test(emailTrimmed) && !emailRecipients.includes(emailTrimmed)) {
                                        setEmailRecipients([...emailRecipients, emailTrimmed]);
                                      } else if (!emailRegex.test(emailTrimmed)) {
                                        toast.error('Invalid billing email address');
                                      } else {
                                        toast.error('Billing email already added');
                                      }
                                    }}
                                    className="text-sm text-slate-600 hover:text-slate-800 underline"
                                    disabled={isEmailLoading}
                                  >
                                    Billing: {email}
                                  </button>
                                ))
                              ) : selectedInvoiceForEmail?.billing_contact_email ? (
                                /* Fallback to legacy single email */
                                <button
                                  onClick={() => {
                                    const email = selectedInvoiceForEmail.billing_contact_email.trim();
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                    if (emailRegex.test(email) && !emailRecipients.includes(email)) {
                                      setEmailRecipients([...emailRecipients, email]);
                                    } else if (!emailRegex.test(email)) {
                                      toast.error('Invalid billing email address');
                                    } else {
                                      toast.error('Billing email already added');
                                    }
                                  }}
                                  className="text-sm text-slate-600 hover:text-slate-800 underline"
                                  disabled={isEmailLoading}
                                >
                                  Billing: {selectedInvoiceForEmail.billing_contact_email}
                                </button>
                              ) : null}
                              {selectedInvoiceForEmail?.shipping_contact_email && (
                                <button
                                  onClick={() => {
                                    const email = selectedInvoiceForEmail.shipping_contact_email.trim();
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                    if (emailRegex.test(email) && !emailRecipients.includes(email)) {
                                      setEmailRecipients([...emailRecipients, email]);
                                    } else if (!emailRegex.test(email)) {
                                      toast.error('Invalid contact email address');
                                    } else {
                                      toast.error('Contact email already added');
                                    }
                                  }}
                                  className="text-sm text-slate-600 hover:text-slate-800 underline"
                                  disabled={isEmailLoading}
                                >
                                  Contact: {selectedInvoiceForEmail.shipping_contact_email}
                                </button>
                              )}
                              {(!selectedInvoiceForEmail?.billing_contact_emails || selectedInvoiceForEmail.billing_contact_emails.length === 0) &&
                               !selectedInvoiceForEmail?.billing_contact_email &&
                               !selectedInvoiceForEmail?.shipping_contact_email && (
                                <p className="text-sm text-slate-500">No customer emails available.</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
                      <button onClick={() => { setIsEmailModalOpen(false); setEmailRecipients([]); setCurrentEmailInput(''); setEmailSuccess(false); setSelectedInvoiceForEmail(null); }} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors" disabled={isEmailLoading}>
                        {emailSuccess ? "Close" : "Cancel"}
                      </button>
                      {!emailSuccess && (
                        <button onClick={handleSendEmail} className="px-4 py-2 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 transition-colors flex items-center disabled:bg-slate-300" disabled={isEmailLoading || emailRecipients.length === 0}>
                          {isEmailLoading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Sending...
                            </>
                          ) : (
                            "Send Email"
                          )}
                        </button>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Export Modal */}
        <Transition appear show={isExportModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeExportModal}>
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                    <div className="border-b border-slate-200 px-4 py-3">
                      <Dialog.Title as="h2" className="text-lg font-semibold text-slate-800">Export Invoices</Dialog.Title>
                    </div>
                    <div className="p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">File Type</label>
                        <select value={fileType} onChange={(e) => setFileType(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400" disabled={isGeneratingExport}>
                          <option value="CSV">CSV</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Customer</label>
                        <select value={selectedCustomerForExport} onChange={(e) => setSelectedCustomerForExport(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400" disabled={isGeneratingExport}>
                          <option value="all">All Customers</option>
                          {getUniqueCustomers().map((customer) => (
                            <option key={customer.customer_id} value={customer.customer_id}>{customer.customer_nickname}</option>
                          ))}
                        </select>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-sm text-sm text-slate-600">
                        <span className="font-medium text-slate-700">Includes:</span> Invoice #, customer, package, PO, amounts, status, dates, payment details.
                      </div>
                    </div>
                    <div className="border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
                      <button onClick={closeExportModal} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors" disabled={isGeneratingExport}>Cancel</button>
                      <button onClick={handleGenerateExport} className="px-4 py-2 text-sm bg-green-600 text-white rounded-sm hover:bg-green-700 transition-colors flex items-center disabled:bg-slate-300" disabled={isGeneratingExport}>
                        {isGeneratingExport ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Generating...
                          </>
                        ) : (
                          "Generate"
                        )}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
    </div>
  );
}

export default Invoices;